import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
  View,
  Text,
  Pressable,
  Platform,
} from 'react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useTheme } from '../../../src/theme/useTheme'
import { JoinFlowLoadingScreen } from '../../../src/join/components/JoinFlowLoadingScreen'
import { JoinFlowScreen } from '../../../src/join/components/JoinFlowScreen'
import { buildApiUrl, API_ENDPOINTS } from '../../../constants/api'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { ErrorBoundary } from '../../../components/layout'
import {
  savePendingDeepLink,
  clearPendingDeepLink,
  isValidDeepLinkType,
  refetchDeepLinkQueries,
  getApiErrorMessage,
} from '../../../src/shared/helpers/deepLink.helper'
import { buildPublicSignInHref } from '../../../src/signIn/helpers/postAuthRedirect.helper'
import { useSnackbar } from '../../../src/shared/hooks/useSnackbar'
import { TYPE_LABELS } from '../../../src/shared/constants/deepLink.constants'
import { getJoinDeepLinkStyles } from '../../../appStyles/_joinDeepLink.styles'
import { TicketStub } from '../../../components/onboarding/TicketStub/TicketStub'
import { SHOW_CONTINUE_WITH_APP_CTA } from '../../../components/onboarding/TicketStub/TicketStub.constants'
import { detectMobileWebOs } from '../../../src/onboarding/helpers/detectMobileWebOs.helper'
import type { TicketStubBrand } from '../../../components/onboarding/TicketStub/TicketStub.types'
import {
  JOIN_CTA_LABELS,
  getSquadInviteFallbackGroupName,
  SQUAD_INVITE_TICKET_COPY,
  formatInvitedByLabel,
} from '../../../appStyles/_joinDeepLink.constants'
import type {
  DeepLinkResolveResponse,
  DeepLinkProcessResponse,
} from '../../../src/shared/types/deepLink.types'

function JoinDeepLinkContent() {
  const appTheme = useTheme()
  const styles = useMemo(() => getJoinDeepLinkStyles(appTheme), [appTheme])
  const zportiBrand: TicketStubBrand = useMemo(
    () => ({
      name: 'Zporti',
      primaryColor: appTheme.primaryDark,
      secondaryColor: appTheme.secondary,
      logoUrl: null,
    }),
    [appTheme.primaryDark, appTheme.secondary],
  )
  const router = useRouter()
  const { type, token } = useLocalSearchParams<{ type: string; token: string }>()
  const { isSignedIn, isLoaded } = useSupabaseAuth()
  const apiClient = useApiClient()
  const apiClientRef = useRef(apiClient)
  apiClientRef.current = apiClient
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const processAttemptedRef = useRef(false)

  const [metadata, setMetadata] = useState<{
    inviterName?: string;
    challengeName?: string | null;
    groupName?: string;
  }>({})
  const [resolving, setResolving] = useState(true)
  const [processing, setProcessing] = useState(false)
  const processingRef = useRef(false)
  const [error, setError] = useState<string | null>(null)

  const isTypeValid = type ? isValidDeepLinkType(type) : false
  const typeConfig = type && isTypeValid ? TYPE_LABELS[type] : null

  useEffect(() => {
    if (type && token && isTypeValid) {
      savePendingDeepLink(type as 'challenge' | 'friend' | 'group', token as string)
    }
  }, [type, token, isTypeValid])

  useEffect(() => {
    if (!token || !isTypeValid) {
      setError('Invalid link')
      setResolving(false)
      return
    }

    const resolveToken = async () => {
      try {
        const url = `${buildApiUrl(API_ENDPOINTS.deepLinks.resolve)}/${token}`
        const response = await axios.get<DeepLinkResolveResponse>(url)
        if (response.data?.success && response.data?.data) {
          setMetadata(response.data.data.metadata)
        } else {
          setError(response.data?.error || 'Link not found')
        }
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to load invite details'))
      } finally {
        setResolving(false)
      }
    }

    resolveToken()
  }, [token, isTypeValid])

  const processLink = useCallback(async () => {
    if (!token || processingRef.current) return
    processingRef.current = true
    setProcessing(true)
    try {
      const response = await apiClientRef.current<DeepLinkProcessResponse>(
        API_ENDPOINTS.deepLinks.process,
        { method: 'POST', data: { token } }
      )
      if (response.data?.success && response.data?.data) {
        await clearPendingDeepLink()
        await refetchDeepLinkQueries(queryClient)
        const { redirectTo, successMessage } = response.data.data
        router.replace((redirectTo || '/app/') as '/')
        if (successMessage) {
          showSnackbar(successMessage, 'success')
        }
      } else {
        setError(response.data?.error || 'Failed to process invite')
        processingRef.current = false
        setProcessing(false)
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to process invite. Please try again later.'))
      processingRef.current = false
      setProcessing(false)
    }
  }, [token, router, queryClient, showSnackbar])

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return
    if (resolving || error) return
    if (processAttemptedRef.current) return
    processAttemptedRef.current = true
    processLink()
  }, [isLoaded, isSignedIn, resolving, error, processLink])

  const handleSignIn = useCallback(() => {
    router.replace('/sign-in')
  }, [router])

  const handleSignUp = useCallback(() => {
    router.replace(buildPublicSignInHref({ signup: true }) as `/${string}`)
  }, [router])

  const handleGoHome = useCallback(() => {
    router.replace('/app/')
  }, [router])

  const loading =
    resolving ||
    !isLoaded ||
    (isLoaded && isSignedIn && !error && (processing || !processAttemptedRef.current))
  if (loading) {
    return <JoinFlowLoadingScreen message="Joining…" />
  }

  if (error || !typeConfig) {
    return (
      <JoinFlowScreen variant="loading">
        <Text style={styles.errorTitle}>{error || 'Invalid Link'}</Text>
        <Text style={styles.errorMessage}>
          This link may have expired or already been used.
        </Text>
        <Pressable
          onPress={handleGoHome}
          style={({ pressed, hovered }) => [
            styles.goHomeButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.goHomeButtonPressed,
            hovered && Platform.OS === 'web' && styles.goHomeButtonHover,
          ]}
        >
          <Text style={styles.goHomeButtonText}>Go Home</Text>
        </Pressable>
      </JoinFlowScreen>
    )
  }

  const inviterName = metadata.inviterName || 'Someone'

  // Squad invites get the full TicketStub tear animation with generic Zporti branding
  if (type === 'group') {
    const groupName = metadata.groupName || getSquadInviteFallbackGroupName()
    return (
      <TicketStub
        brand={zportiBrand}
        presentedBy={formatInvitedByLabel(inviterName)}
        title={groupName}
        subtitle={SQUAD_INVITE_TICKET_COPY.SUBTITLE}
        memberCount={0}
        memberCountLabel=""
        groupCode={null}
        primaryCta={{ label: JOIN_CTA_LABELS.CREATE_ACCOUNT, onPress: handleSignUp }}
        disabledNotice={null}
        secondaryCta={{ label: JOIN_CTA_LABELS.ALREADY_HAVE_ACCOUNT_SIGN_IN, onPress: handleSignIn }}
        showOpenInApp={
          SHOW_CONTINUE_WITH_APP_CTA && detectMobileWebOs() !== null
        }
      />
    )
  }

  return (
    <JoinFlowScreen>
      <View style={styles.container}>
        <View style={styles.card}>
          <View style={styles.iconCircle}>
            <Text style={styles.iconText}>{typeConfig.icon}</Text>
          </View>

          <Text style={styles.title}>{typeConfig.title}</Text>

          <Text style={styles.subtitle}>
            <Text style={styles.subtitleBold}>{inviterName}</Text>
            {type === 'challenge' && metadata.challengeName
              ? ` challenged you: ${metadata.challengeName}`
              : ' has invited you'}
          </Text>

          <Text style={styles.description}>{typeConfig.description}</Text>

          <Pressable
            onPress={handleSignIn}
            style={({ pressed, hovered }) => [
              styles.primaryButton,
              styles.buttonSpacing,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.primaryButtonPressed,
              hovered && Platform.OS === 'web' && styles.primaryButtonHover,
            ]}
          >
            <Text style={styles.primaryButtonText}>Sign In to Accept</Text>
          </Pressable>

          <Pressable
            onPress={handleSignUp}
            style={({ pressed, hovered }) => [
              styles.secondaryButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.secondaryButtonPressed,
              hovered && Platform.OS === 'web' && styles.secondaryButtonHover,
            ]}
          >
            <Text style={styles.secondaryButtonText}>Create Account</Text>
          </Pressable>
        </View>
      </View>
    </JoinFlowScreen>
  )
}

export default function JoinDeepLink() {
  return (
    <ErrorBoundary>
      <JoinDeepLinkContent />
    </ErrorBoundary>
  )
}
